import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Button,
  Container,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Collapse,
  IconButton,
  ListSubheader,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { linkTo } from '../utils/linkTo';

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'scrolled',
})<{ scrolled?: boolean }>(({ scrolled }) => ({
  background: scrolled 
    ? 'rgba(0, 0, 0, 0.95)' 
    : 'rgba(0, 0, 0, 0.3)',
  backdropFilter: scrolled ? 'blur(20px)' : 'blur(5px)',
  boxShadow: scrolled 
    ? '0 4px 30px rgba(8, 145, 178, 0.2), 0 0 1px rgba(8, 145, 178, 0.5)' 
    : 'none',
  borderBottom: scrolled 
    ? '1px solid rgba(8, 145, 178, 0.3)' 
    : '1px solid transparent',
  transition: 'all 0.3s ease',
  position: 'fixed',
  top: 0,
  zIndex: 1100,
}));

const LogoContainer = styled('div')({
  transition: 'transform 0.2s ease',
  '&:hover': { transform: 'scale(1.05)' },
  '&:active': { transform: 'scale(0.95)' },
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  position: 'relative',
});

const MenuIconSwap = styled('span')({
  display: 'inline-flex',
  animation: 'menuIconIn 0.2s ease',
  '&.is-open': { animationName: 'menuIconInOpen' },
  '@keyframes menuIconIn': { from: { transform: 'rotate(90deg)', opacity: 0 }, to: { transform: 'none', opacity: 1 } },
  '@keyframes menuIconInOpen': { from: { transform: 'rotate(-90deg)', opacity: 0 }, to: { transform: 'none', opacity: 1 } },
});

const LogoText = styled('span')({
  fontFamily: '"Orbitron", sans-serif',
  fontSize: '1.8rem',
  fontWeight: 900,
  color: '#0891b2',
  textTransform: 'uppercase',
  letterSpacing: '0.15em',
  position: 'relative',
});

const NavButton = styled(Button)(({ theme }) => ({
  color: '#0891b2',
  fontSize: '0.95rem',
  fontWeight: 600,
  fontFamily: '"Rajdhani", sans-serif',
  letterSpacing: '0.08em',
  padding: '8px 16px',
  whiteSpace: 'nowrap',
  minWidth: 0,
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.between('lg', 'xl')]: {
    padding: '8px 10px',
    letterSpacing: '0.05em',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    width: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #0891b2, #64748b)',
    transform: 'translateX(-50%)',
    transition: 'width 0.3s ease',
  },
  '&:hover': {
    backgroundColor: 'rgba(8, 145, 178, 0.05)',
    '&::before': {
      width: '80%',
    },
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.98)',
    backdropFilter: 'blur(20px)',
    border: '2px solid rgba(8, 145, 178, 0.5)',
    borderRadius: 0,
    boxShadow: '0 0 30px rgba(8, 145, 178, 0.3), inset 0 0 20px rgba(8, 145, 178, 0.05)',
    color: '#fff',
    marginTop: '12px',
    '& .MuiMenuItem-root': {
      fontFamily: '"Rajdhani", sans-serif',
      fontSize: '1rem',
      fontWeight: 600,
      padding: '12px 24px',
      borderBottom: '1px solid rgba(8, 145, 178, 0.1)',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: 'rgba(8, 145, 178, 0.1)',
        paddingLeft: '32px',
        borderLeft: '3px solid #0891b2',
      },
      '&:last-child': {
        borderBottom: 'none',
      },
    },
  },
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 300,
    backgroundColor: 'rgba(0, 0, 0, 0.98)',
    backdropFilter: 'blur(20px)',
    borderLeft: '2px solid rgba(8, 145, 178, 0.5)',
    boxShadow: '-10px 0 30px rgba(8, 145, 178, 0.2)',
    color: '#fff',
  },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  color: '#0891b2',
  border: '2px solid rgba(8, 145, 178, 0.5)',
  borderRadius: 0,
  padding: '8px',
  '&:hover': {
    backgroundColor: 'rgba(8, 145, 178, 0.1)',
    borderColor: '#0891b2',
    boxShadow: '0 0 20px rgba(8, 145, 178, 0.5)',
  },
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}));

const ContactButton = styled(Button)({
  background: 'linear-gradient(135deg, #0891b2, #64748b)',
  color: '#000000',
  fontWeight: 700,
  padding: '10px 24px',
  whiteSpace: 'nowrap',
  borderRadius: 0,
  border: '2px solid transparent',
  boxShadow: '0 0 20px rgba(8, 145, 178, 0.5)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
    transition: 'left 0.5s',
  },
  '&:hover': {
    background: 'linear-gradient(135deg, #00CCCC, #7B2CBF)',
    boxShadow: '0 0 30px rgba(8, 145, 178, 0.8), 0 0 60px rgba(100, 116, 139, 0.4)',
    transform: 'translateY(-2px)',
    '&::before': {
      left: '100%',
    },
  },
});

const DesktopMenu = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

interface ServiceLink {
  label: string;
  url: string;
}

interface ServiceGroup {
  group: string;
  items: ServiceLink[];
}

const serviceGroups: ServiceGroup[] = [
  {
    group: 'Microsoft & Business Apps',
    items: [
      { label: 'Dynamics 365', url: '/services/dynamics-365' },
      { label: 'Microsoft 365 & Azure Admin', url: '/services/microsoft-365-azure' },
      { label: 'CRM Solutions', url: '/services/crm-solutions' },
      { label: 'AI & Automation', url: '/services/ai-automation' },
    ],
  },
  {
    group: 'Managed IT',
    items: [
      { label: 'Managed IT & Help Desk', url: '/services/managed-it-services' },
      { label: 'Cybersecurity', url: '/services/cybersecurity' },
      { label: 'Cloud & Azure', url: '/services/cloud-migration' },
      { label: 'Infrastructure', url: '/services/infrastructure-management' },
      { label: 'Backup & Recovery', url: '/services/backup-disaster-recovery' },
      { label: 'VoIP', url: '/services/voip-communications' },
      { label: 'Network Cabling', url: '/services/network-cabling' },
    ],
  },
  {
    group: 'Development',
    items: [{ label: 'Software & Web Development', url: '/services/software-solutions' }],
  },
];

const FuturisticNavbar = () => {
  const [anchorEl, setAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, menuName: string) => {
    setAnchorEl({ ...anchorEl, [menuName]: event.currentTarget });
  };

  const handleMenuClose = (menuName: string) => {
    setAnchorEl({ ...anchorEl, [menuName]: null });
  };

  const handleMobileMenuToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileServicesToggle = () => {
    setMobileServicesOpen(!mobileServicesOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
  };

  return (
    <StyledAppBar position="fixed" scrolled={scrolled}>
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: '70px' }}>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <LogoContainer>
              <LogoText data-text="VITNOLOGY">VITNOLOGY</LogoText>
            </LogoContainer>
          </Link>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Mobile Menu Button */}
            <MobileMenuButton
              onClick={handleMobileMenuToggle}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              <MenuIconSwap key={mobileOpen ? 'close' : 'menu'} className={mobileOpen ? 'is-open' : ''}>
                {mobileOpen ? <CloseIcon /> : <MenuIcon />}
              </MenuIconSwap>
            </MobileMenuButton>

            {/* Desktop Menu */}
            <DesktopMenu>
              <NavButton {...linkTo("/")}>Home</NavButton>

              <NavButton {...linkTo("/services/dynamics-365")}>Dynamics 365</NavButton>

              <Box>
                <NavButton
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={(e) => handleMenuOpen(e, 'Services')}
                >
                  Services
                </NavButton>
                <StyledMenu
                  anchorEl={anchorEl.Services}
                  open={Boolean(anchorEl.Services)}
                  onClose={() => handleMenuClose('Services')}
                  MenuListProps={{ 'aria-label': 'Services' }}
                >
                  {serviceGroups.flatMap(({ group, items }) => [
                    <ListSubheader
                      key={group}
                      sx={{
                        background: 'transparent',
                        color: '#64748b',
                        fontFamily: '"Rajdhani", sans-serif',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        lineHeight: '32px',
                        mt: 1,
                      }}
                    >
                      {group}
                    </ListSubheader>,
                    ...items.map((item) => (
                      <MenuItem
                        key={item.url}
                        component={Link}
                        to={item.url}
                        onClick={() => handleMenuClose('Services')}
                      >
                        {item.label}
                      </MenuItem>
                    )),
                  ])}
                </StyledMenu>
              </Box>

              <NavButton {...linkTo("/how-we-work")}>How We Work</NavButton>

              <NavButton {...linkTo("/about")}>About</NavButton>

              <NavButton {...linkTo("/blog")}>Resources</NavButton>

              <ContactButton {...linkTo("/contact")}>Contact Us</ContactButton>
            </DesktopMenu>
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <MobileDrawer
        anchor="right"
        open={mobileOpen}
        onClose={handleMobileMenuClose}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Box sx={{ width: 300, height: '100%', pt: 3, px: 2 }}>
          <List>
            {/* Home */}
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton 
                component={Link} 
                to="/" 
                onClick={handleMobileMenuClose}
                sx={{
                  borderLeft: '3px solid transparent',
                  '&:hover': {
                    borderLeft: '3px solid #0891b2',
                    backgroundColor: 'rgba(8, 145, 178, 0.1)',
                    paddingLeft: '20px',
                  },
                }}
              >
                <ListItemText 
                  primary="Home" 
                  sx={{ 
                    color: '#0891b2',
                    '& .MuiListItemText-primary': {
                      fontFamily: '"Rajdhani", sans-serif',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                    }
                  }} 
                />
              </ListItemButton>
            </ListItem>

            {/* About */}
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                component={Link}
                to="/about"
                onClick={handleMobileMenuClose}
                sx={{
                  borderLeft: '3px solid transparent',
                  '&:hover': {
                    borderLeft: '3px solid #0891b2',
                    backgroundColor: 'rgba(8, 145, 178, 0.1)',
                    paddingLeft: '20px',
                  },
                }}
              >
                <ListItemText
                  primary="About"
                  sx={{
                    color: '#0891b2',
                    '& .MuiListItemText-primary': {
                      fontFamily: '"Rajdhani", sans-serif',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>

            {/* Dynamics 365 */}
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                component={Link}
                to="/services/dynamics-365"
                onClick={handleMobileMenuClose}
                sx={{
                  borderLeft: '3px solid transparent',
                  '&:hover': {
                    borderLeft: '3px solid #0891b2',
                    backgroundColor: 'rgba(8, 145, 178, 0.1)',
                    paddingLeft: '20px',
                  },
                }}
              >
                <ListItemText
                  primary="Dynamics 365"
                  sx={{
                    color: '#0891b2',
                    '& .MuiListItemText-primary': {
                      fontFamily: '"Rajdhani", sans-serif',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>

            {/* Resources (Blog) */}
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                component={Link}
                to="/blog"
                onClick={handleMobileMenuClose}
                sx={{
                  borderLeft: '3px solid transparent',
                  '&:hover': {
                    borderLeft: '3px solid #0891b2',
                    backgroundColor: 'rgba(8, 145, 178, 0.1)',
                    paddingLeft: '20px',
                  },
                }}
              >
                <ListItemText
                  primary="Resources"
                  sx={{
                    color: '#0891b2',
                    '& .MuiListItemText-primary': {
                      fontFamily: '"Rajdhani", sans-serif',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>

            {/* How We Work */}
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                component={Link}
                to="/how-we-work"
                onClick={handleMobileMenuClose}
                sx={{
                  borderLeft: '3px solid transparent',
                  '&:hover': {
                    borderLeft: '3px solid #0891b2',
                    backgroundColor: 'rgba(8, 145, 178, 0.1)',
                    paddingLeft: '20px',
                  },
                }}
              >
                <ListItemText
                  primary="How We Work"
                  sx={{
                    color: '#0891b2',
                    '& .MuiListItemText-primary': {
                      fontFamily: '"Rajdhani", sans-serif',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>

            {/* Services with Collapse */}
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton 
                onClick={handleMobileServicesToggle}
                sx={{
                  borderLeft: '3px solid transparent',
                  '&:hover': {
                    borderLeft: '3px solid #64748b',
                    backgroundColor: 'rgba(100, 116, 139, 0.1)',
                    paddingLeft: '20px',
                  },
                }}
              >
                <ListItemText 
                  primary="Services" 
                  sx={{ 
                    color: '#64748b',
                    '& .MuiListItemText-primary': {
                      fontFamily: '"Rajdhani", sans-serif',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                    }
                  }} 
                />
                {mobileServicesOpen ? 
                  <ExpandLessIcon sx={{ color: '#64748b' }} /> : 
                  <ExpandMoreIcon sx={{ color: '#64748b' }} />
                }
              </ListItemButton>
            </ListItem>
            <Collapse in={mobileServicesOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {serviceGroups.map(({ group, items }) => (
                  <React.Fragment key={group}>
                    <ListSubheader
                      sx={{
                        background: 'transparent',
                        color: '#64748b',
                        fontFamily: '"Rajdhani", sans-serif',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        lineHeight: '32px',
                        pl: 4,
                      }}
                    >
                      {group}
                    </ListSubheader>
                    {items.map((item) => (
                      <ListItem key={item.url} disablePadding>
                        <ListItemButton
                          component={Link}
                          to={item.url}
                          onClick={handleMobileMenuClose}
                          sx={{
                            pl: 4,
                            borderLeft: '3px solid transparent',
                            '&:hover': {
                              borderLeft: '3px solid #10b981',
                              backgroundColor: 'rgba(16, 185, 129, 0.05)',
                              paddingLeft: '36px',
                            },
                          }}
                        >
                          <ListItemText
                            primary={item.label}
                            sx={{
                              color: 'rgba(255, 255, 255, 0.8)',
                              '& .MuiListItemText-primary': {
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                fontSize: '0.95rem',
                                fontWeight: 500,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </React.Fragment>
                ))}
              </List>
            </Collapse>

            {/* Contact Us */}
            <ListItem disablePadding sx={{ mt: 3 }}>
              <ListItemButton 
                component={Link} 
                to="/contact" 
                onClick={handleMobileMenuClose}
                sx={{
                  background: 'linear-gradient(135deg, #0891b2, #64748b)',
                  color: '#000000',
                  fontWeight: 700,
                  mx: 1,
                  border: '2px solid rgba(8, 145, 178, 0.5)',
                  boxShadow: '0 0 20px rgba(8, 145, 178, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #00CCCC, #7B2CBF)',
                    boxShadow: '0 0 30px rgba(8, 145, 178, 0.6)',
                  },
                }}
              >
                <ListItemText 
                  primary="Contact Us" 
                  sx={{ 
                    textAlign: 'center',
                    '& .MuiListItemText-primary': {
                      fontFamily: '"Rajdhani", sans-serif',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                    }
                  }} 
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </MobileDrawer>
    </StyledAppBar>
  );
};

export default FuturisticNavbar;

